// 1Y-pp-filter: Parent-product (PP_Display) multiselect with popover.
// Mirrors VendorMultiselect's structure exactly — same popover, same
// portal, same search/Select-all/Clear footer — but reads the parent
// product list from /api/dashboard/filters' new `parentProducts` field
// and writes into the filterStore's `parentProducts` Set via toggleParent.
// Renders an explicit "(no parent products available)" when the list is
// empty so it never looks like a silent failure.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { FiltersResponse } from "../types";
import { useFilterStore } from "../state/filterStore";

interface PopoverPosition {
  top: number;
  left: number;
  width: number;
}

export function ParentProductMultiselect(): JSX.Element {
  const parentProducts = useFilterStore((s) => s.parentProducts);
  const toggleParent = useFilterStore((s) => s.toggleParent);

  const filtersQuery = useQuery<FiltersResponse>({
    queryKey: ["filters"],
    queryFn: ({ signal }) =>
      api.get<FiltersResponse>("/dashboard/filters", signal),
    staleTime: 5 * 60_000,
  });

  const parentOptions = useMemo(
    () => filtersQuery.data?.parentProducts ?? [],
    [filtersQuery.data?.parentProducts],
  );

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<PopoverPosition | null>(null);

  // Position the popover anchored to the button. Recomputed on open /
  // viewport changes.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const btn = buttonRef.current;
    if (!btn) return;
    const place = () => {
      const r = btn.getBoundingClientRect();
      setPos({
        top: r.bottom + 4,
        left: r.left,
        width: Math.max(r.width, 260),
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // Close on outside click + escape.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (buttonRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return parentOptions;
    return parentOptions.filter((p) => p.name.toLowerCase().includes(q));
  }, [parentOptions, search]);

  const selectedCount = parentProducts.size;
  const buttonLabel =
    selectedCount === 0
      ? "Parent Product"
      : selectedCount === 1
        ? `Parent Product (1 selected)`
        : `Parent Product (${selectedCount} selected)`;

  const onSelectAll = useCallback(() => {
    // Replace the parentProducts Set wholesale — same escape-hatch pattern
    // VendorMultiselect uses to avoid widening the store action surface.
    useFilterStore.setState({
      parentProducts: new Set(filtered.map((p) => p.name)),
    });
  }, [filtered]);

  const onClear = useCallback(() => {
    useFilterStore.setState({ parentProducts: new Set<string>() });
  }, []);

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────

  const popover =
    open && pos
      ? createPortal(
          <div
            ref={popRef}
            className="dash-vendor-popover dash-card"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
            }}
            role="dialog"
            aria-label="Parent product filter"
          >
            <input
              type="text"
              className="dash-control dash-vendor-popover__search"
              placeholder="Search parent products…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <div className="dash-vendor-popover__list">
              {filtersQuery.isLoading ? (
                <div className="dash-vendor-popover__empty dash-small">
                  Loading…
                </div>
              ) : filtersQuery.isError ? (
                <div className="dash-vendor-popover__empty dash-small">
                  Failed to load parent products.
                </div>
              ) : parentOptions.length === 0 ? (
                <div className="dash-vendor-popover__empty dash-small">
                  (no parent products available)
                </div>
              ) : filtered.length === 0 ? (
                <div className="dash-vendor-popover__empty dash-small">
                  No parent products match "{search}".
                </div>
              ) : (
                filtered.map((p) => {
                  const checked = parentProducts.has(p.name);
                  return (
                    <label
                      key={p.name}
                      className="dash-vendor-popover__row"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleParent(p.name)}
                      />
                      <span className="dash-vendor-popover__name">
                        {p.name}
                      </span>
                      <span className="dash-vendor-popover__count dash-subtle dash-num">
                        {p.count}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
            <div className="dash-vendor-popover__footer">
              <button
                type="button"
                className="dash-button"
                onClick={onSelectAll}
                disabled={filtered.length === 0}
              >
                Select all
              </button>
              <button
                type="button"
                className="dash-button"
                onClick={onClear}
                disabled={selectedCount === 0}
              >
                Clear
              </button>
            </div>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="dash-filter-field">
      <span className="dash-filter-field__header">Parent Product</span>
      <button
        ref={buttonRef}
        type="button"
        className="dash-vendor-button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="dash-vendor-button__label">{buttonLabel}</span>
        <span aria-hidden className="dash-vendor-button__caret">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {popover}
    </div>
  );
}
